import { useEffect, useState, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Brain, Compass, Globe, GraduationCap, MessageCircle, 
  Sparkles, BookOpen, Ship, Anchor, X, Radio,
  Sailboat, Users, MapPin, ChevronRight, Waves, Award
} from "lucide-react";
import { CaptainLogo } from "@/components/CaptainLogo";
import { CaptainRocaAI } from "@/components/CaptainRocaAI";
import { CRRFlashTicker } from "@/components/CRRFlashTicker";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CaptainRocaLinks } from "@/components/CaptainRocaLinks";

import { QuickAccessHub } from "@/components/QuickAccessHub";
import { SEO } from "@/components/SEO";
import { RecommendedJourney } from "@/components/RecommendedJourney";
import { FloatingFilmReel } from "@/components/FloatingFilmReel";

// Lazy-load heavy below-the-fold components for faster FCP
const HistoricShipsExplorer = lazy(() => import("@/components/HistoricShipsExplorer"));
const DayInTheLife = lazy(() => import("@/components/DayInTheLife"));
const MaritimeSalaryCalculator = lazy(() => import("@/components/MaritimeSalaryCalculator"));
const MaritimeJobLinks = lazy(() => import("@/components/MaritimeJobLinks").then(m => ({ default: m.MaritimeJobLinks })));
const SeasonalMaritimeJobsMap = lazy(() => import("@/components/SeasonalMaritimeJobsMap").then(m => ({ default: m.SeasonalMaritimeJobsMap })));
const WatersportActivitiesCatalog = lazy(() => import("@/components/WatersportActivitiesCatalog"));
const IdeaBox = lazy(() => import("@/components/IdeaBox"));
const SupportCreditsDonation = lazy(() => import("@/components/SupportCreditsDonation"));
const DyslexiaSOS = lazy(() => import("@/components/DyslexiaSOS"));
const DyslexicLeadersCarousel = lazy(() => import("@/components/DyslexicLeadersCarousel"));
const MaritimePathwayIcons = lazy(() => import("@/components/MaritimePathwayIcons"));
const FranceMaritimeEventsCalendar = lazy(() => import("@/components/FranceMaritimeEventsCalendar"));

const Index = () => {
  const [lang, setLang] = useState("en");
  const [showCaptainRoca, setShowCaptainRoca] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | undefined>();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Captain ROCA | AI Maritime Companion for Dyslexic Minds";
    document.querySelector('meta[name="description"]')?.setAttribute('content', 
      'Captain ROCA — Your AI Companion for maritime career guidance, CRR exam prep, and dyslexic-friendly learning. Study to become a Captain or Watersports Instructor.'
    );
  }, []);

  const t = (en: string, fr: string, es?: string) => {
    if (lang === "fr") return fr;
    if (lang === "es") return es || en;
    return en;
  };

  const openChatWithPrompt = (prompt: string) => {
    setPendingPrompt(prompt);
    setShowCaptainRoca(true);
  };

  const corePillars = [
    {
      icon: GraduationCap,
      title: t('Study Support & Exam Prep', 'Aide aux études & Préparation aux examens', 'Apoyo al estudio & Preparación de exámenes'),
      description: t(
        'Struggling with maritime certifications? Captain ROCA breaks down complex regulations into bite-sized, dyslexia-friendly lessons.',
        'Des difficultés avec les certifications maritimes ? Captain ROCA simplifie les réglementations en leçons adaptées à la dyslexie.',
        '¿Dificultades con las certificaciones marítimas? Captain ROCA simplifica las regulaciones en lecciones adaptadas a la dislexia.'
      ),
      accent: 'text-accent',
      borderAccent: 'border-accent/30',
    },
    {
      icon: Compass,
      title: t('Career Navigation', 'Navigation de Carrière', 'Navegación Profesional'),
      description: t(
        'Deckhand, watersports instructor, or yacht steward? Captain ROCA maps your career pathway step by step.',
        'Matelot, moniteur de sports nautiques ou steward ? Captain ROCA trace votre parcours étape par étape.',
        '¿Marinero, instructor de deportes acuáticos o steward? Captain ROCA traza tu camino profesional paso a paso.'
      ),
      accent: 'text-neon-purple',
      borderAccent: 'border-neon-purple/30',
    },
    {
      icon: Globe,
      title: t('Country & Market Intelligence', 'Intelligence Pays & Marchés', 'Inteligencia de Países y Mercados'),
      description: t(
        'France for sailing schools. Spain for charter seasons. England for superyacht academies. Captain ROCA knows where to go.',
        'La France pour les écoles de voile. L\'Espagne pour les saisons charter. L\'Angleterre pour les académies superyacht.',
        'Francia para escuelas de vela. España para temporadas de chárter. Inglaterra para academias de superyates. Captain ROCA sabe adónde ir.'
      ),
      accent: 'text-primary',
      borderAccent: 'border-primary/30',
    },
    {
      icon: Brain,
      title: t('Dyslexic Thinker Empowerment', 'Empowerment des Penseurs Dyslexiques', 'Empoderamiento del Pensador Disléxico'),
      description: t(
        'Visual memory hooks, icon-based logic, and zero walls of text. Because dyslexia is a superpower at sea.',
        'Ancrages visuels, logique par icônes, zéro murs de texte. Parce que la dyslexie est un superpouvoir en mer.',
        'Anclas visuales de memoria, lógica por iconos y cero muros de texto. Porque la dislexia es un superpoder en el mar.'
      ),
      accent: 'text-accent',
      borderAccent: 'border-accent/30',
    },
  ];

  const countrySpotlights = [
    {
      flag: '🇫🇷',
      country: 'France',
      focus: t('CRR Licence • BPJEPS • Sailing Schools', 'Licence CRR • BPJEPS • Écoles de voile', 'Licencia CRR • BPJEPS • Escuelas de vela'),
      detail: t(
        'Official maritime training hub. Captain ROCA guides you through French certification pathways.',
        'Plaque tournante de la formation maritime. Captain ROCA vous guide dans les parcours de certification français.',
        'Centro oficial de formación marítima. Captain ROCA te guía en los itinerarios de certificación franceses.'
      ),
    },
    {
      flag: '🇪🇸',
      country: t('Spain', 'Espagne', 'España'),
      focus: t('Charter Season • Balearics • Water Sports', 'Saison charter • Baléares • Sports nautiques', 'Temporada chárter • Baleares • Deportes acuáticos'),
      detail: t(
        'Peak season careers in Mallorca, Ibiza, and Barcelona. Crew house connections and seasonal contracts.',
        'Carrières de haute saison à Majorque, Ibiza et Barcelone. Connexions et contrats saisonniers.',
        'Carreras de temporada alta en Mallorca, Ibiza y Barcelona. Conexiones y contratos estacionales.'
      ),
    },
    {
      flag: '🇬🇧',
      country: t('England', 'Angleterre', 'Inglaterra'),
      focus: t('Superyacht Academy • RYA • MCA Certs', 'Académie Superyacht • RYA • Certifications MCA', 'Academia Superyate • RYA • Certificaciones MCA'),
      detail: t(
        'World-class superyacht training. MCA and RYA certification roadmaps.',
        'Formation superyacht de classe mondiale. Feuilles de route des certifications MCA et RYA.',
        'Formación de superyates de clase mundial. Hojas de ruta de certificaciones MCA y RYA.'
      ),
    },
  ];

  const maritimePaths = [
    {
      id: 'sailing', icon: '⛵', label: t('Sailing', 'Voilier / Catamaran', 'Velero / Catamarán'),
      color: 'border-accent/50 bg-accent/5', activeColor: 'border-accent bg-accent/15 ring-2 ring-accent/30',
      description: t('Navigate under sail — from coastal cruises to transatlantic crossings.', 'Naviguer à la voile — des croisières côtières aux traversées transatlantiques.', 'Navegar a vela — desde cruceros costeros hasta travesías transatlánticas.'),
      licenses: t('Permis Hauturier • CRR • RYA Yachtmaster', 'Permis Hauturier • CRR • RYA Yachtmaster', 'Permiso de Altura • CRR • RYA Yachtmaster'),
      firstSteps: t('Sailing school → Coastal miles → Delivery crew', 'École de voile → Milles côtiers → Convoyage', 'Escuela de vela → Millas costeras → Entrega'),
    },
    {
      id: 'yacht-crew', icon: '🛥', label: t('Yacht Crew', 'Équipage Yacht', 'Tripulación Yate'),
      color: 'border-neon-purple/50 bg-neon-purple/5', activeColor: 'border-neon-purple bg-neon-purple/15 ring-2 ring-neon-purple/30',
      description: t('Work on superyachts as deckhand, steward, or captain.', 'Travailler sur des superyachts comme matelot, steward ou capitaine.', 'Trabajar en superyates como marinero, steward o capitán.'),
      licenses: t('STCW • CRR • Powerboat L2 • ENG1', 'STCW • CRR • Powerboat L2 • ENG1', 'STCW • CRR • Powerboat L2 • ENG1'),
      firstSteps: t('STCW course → Crew agency → First season', 'Formation STCW → Agence crew → Première saison', 'Curso STCW → Agencia crew → Primera temporada'),
    },
    {
      id: 'watersports', icon: '🏄', label: t('Watersports Instructor', 'Moniteur Nautique', 'Instructor Acuático'),
      color: 'border-primary/50 bg-primary/5', activeColor: 'border-primary bg-primary/15 ring-2 ring-primary/30',
      description: t('Teach jet-ski, wakeboard, paddle, or sailing on yachts and bases.', 'Enseigner jet-ski, wakeboard, paddle ou voile sur yachts et bases.', 'Enseñar jet-ski, wakeboard, paddle o vela en yates y bases.'),
      licenses: t('BPJEPS Nautisme • CRR • Permis Côtier', 'BPJEPS Nautisme • CRR • Permis Côtier', 'BPJEPS Nautismo • CRR • Licencia Costera'),
      firstSteps: t('Permis Côtier → CRR → BPJEPS (10 months)', 'Permis Côtier → CRR → BPJEPS (10 mois)', 'Licencia Costera → CRR → BPJEPS (10 meses)'),
    },
    {
      id: 'jetski-xtrem', icon: '🚤', label: t('Jet-ski / Xtrem', 'Jet-ski / Xtrem', 'Jet-ski / Xtrem'),
      color: 'border-destructive/50 bg-destructive/5', activeColor: 'border-destructive bg-destructive/15 ring-2 ring-destructive/30',
      description: t('High-adrenaline watersports — jet-ski, flyboard, e-foil.', 'Sports nautiques extrêmes — jet-ski, flyboard, e-foil.', 'Deportes acuáticos extremos — jet-ski, flyboard, e-foil.'),
      licenses: t('Permis Côtier • CRR • BPJEPS option motonautisme', 'Permis Côtier • CRR • BPJEPS option motonautisme', 'Licencia Costera • CRR • BPJEPS opción motonáutica'),
      firstSteps: t('Permis Côtier → Practice → BPJEPS specialization', 'Permis Côtier → Pratique → Spécialisation BPJEPS', 'Licencia Costera → Práctica → Especialización BPJEPS'),
    },
    {
      id: 'fishing', icon: '🎣', label: t('Fishing / Matelot', 'Pêche / Matelot', 'Pesca / Marinero'),
      color: 'border-warning/50 bg-warning/5', activeColor: 'border-warning bg-warning/15 ring-2 ring-warning/30',
      description: t('Traditional maritime work on fishing boats and coastal vessels.', 'Métier traditionnel sur les bateaux de pêche et navires côtiers.', 'Trabajo tradicional en barcos pesqueros y buques costeros.'),
      licenses: t('Certificat Matelot Pont • CRR • STCW', 'Certificat Matelot Pont • CRR • STCW', 'Certificado Marinero • CRR • STCW'),
      firstSteps: t('CRR → Matelot certificate → Fishing fleet', 'CRR → Certificat matelot → Flotte de pêche', 'CRR → Certificado marinero → Flota pesquera'),
    },
    {
      id: 'snsm', icon: '🛟', label: t('SNSM Volunteer', 'Volontaire SNSM', 'Voluntario SNSM'),
      color: 'border-accent/50 bg-accent/5', activeColor: 'border-accent bg-accent/15 ring-2 ring-accent/30',
      description: t('Join France\'s sea rescue volunteers — save lives at sea.', 'Rejoindre les sauveteurs en mer bénévoles — sauver des vies.', 'Únete a los voluntarios de rescate marítimo — salva vidas en el mar.'),
      licenses: t('Permis Côtier • CRR • First Aid (PSE1)', 'Permis Côtier • CRR • Premiers secours (PSE1)', 'Licencia Costera • CRR • Primeros auxilios (PSE1)'),
      firstSteps: t('Contact local SNSM station → Training → Operations', 'Contacter station SNSM locale → Formation → Opérations', 'Contactar estación SNSM local → Formación → Operaciones'),
    },
    {
      id: 'marine-protection', icon: '🐬', label: t('Marine Protection', 'Protection Marine', 'Protección Marina'),
      color: 'border-success/50 bg-success/5', activeColor: 'border-success bg-success/15 ring-2 ring-success/30',
      description: t('Ocean conservation — patrol, research, environmental education.', 'Conservation des océans — patrouille, recherche, éducation environnementale.', 'Conservación oceánica — patrulla, investigación, educación ambiental.'),
      licenses: t('Diving certs • CRR • Permis Côtier • BPJEPS', 'Certifications plongée • CRR • Permis Côtier • BPJEPS', 'Certificaciones buceo • CRR • Licencia Costera • BPJEPS'),
      firstSteps: t('Volunteer with Sea Shepherd → Build certs → Career', 'Bénévolat Sea Shepherd → Certifications → Carrière', 'Voluntariado Sea Shepherd → Certificaciones → Carrera'),
    },
    {
      id: 'fluvial', icon: '🏞', label: t('River Tourism', 'Tourisme Fluvial', 'Turismo Fluvial'),
      color: 'border-primary/50 bg-primary/5', activeColor: 'border-primary bg-primary/15 ring-2 ring-primary/30',
      description: t('Cruise the rivers and canals of Europe — barges, riverboats, tourism.', 'Naviguer sur les fleuves et canaux d\'Europe — péniches, bateaux fluviaux.', 'Navegar por los ríos y canales de Europa — barcazas, barcos fluviales.'),
      licenses: t('Permis Fluvial • CRR (optional)', 'Permis Fluvial • CRR (optionnel)', 'Licencia Fluvial • CRR (opcional)'),
      firstSteps: t('Permis Fluvial → River tourism company → Experience', 'Permis Fluvial → Entreprise tourisme fluvial → Expérience', 'Licencia Fluvial → Empresa turismo fluvial → Experiencia'),
    },
  ];

  const selectedPathData = maritimePaths.find(p => p.id === selectedPath);

  return (
    <div className="min-h-screen w-full bg-background text-foreground">
      <SEO title="Captain ROCA | AI Maritime Companion for Dyslexic Minds" description="Free AI guide to start a maritime career — CRR, Permis Côtier & Fluvial, BPJEPS Motonautisme, Captain pathways. Built for dyslexic thinkers." path="/" ogType="website" />

      {/* ─── NAVBAR ─── */}
      <Navbar
        lang={lang}
        setLang={setLang}
        onOpenChat={() => { setPendingPrompt(undefined); setShowCaptainRoca(true); }}
        t={t}
      />

      {/* ─── HERO SECTION ─── */}
      <section id="hero" className="relative w-full min-h-screen overflow-hidden flex items-center justify-center">
        <video
          autoPlay loop muted playsInline
          className="absolute inset-0 h-full w-full object-cover object-[center_20%] sm:object-center"
        >
          <source src="/videos/captain-roca-hero.mp4" type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/80" />

        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center pt-24 pb-16">
          <p className="uppercase tracking-[0.3em] text-xs md:text-sm text-white/80 mb-6">
            {t('Built for dyslexic minds. Made for the ocean.', 'Conçu pour les esprits dyslexiques. Fait pour l\'océan.', 'Diseñado para mentes disléxicas. Hecho para el océano.')}
          </p>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight text-white mb-4">
            Captain ROCA
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-medium mb-3">
            {t('Your AI Maritime Companion', 'Votre Compagnon Maritime IA', 'Tu Compañero Marítimo IA')}
          </p>

          <p className="text-sm sm:text-base text-white/70 max-w-xl mx-auto mb-10 leading-relaxed">
            {t(
              'A free AI guide to help you start a career at sea. Study for your boat license. Find your first maritime job.',
              'Un guide IA gratuit pour démarrer une carrière en mer. Préparez votre permis bateau. Trouvez votre premier emploi.',
              'Una guía IA gratuita para empezar tu carrera en el mar. Prepara tu licencia náutica. Encuentra tu primer trabajo.'
            )}
          </p>

          {/* 2 CTA buttons side by side */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <button
              onClick={() => { setPendingPrompt(undefined); setShowCaptainRoca(true); }}
              className="flex items-center gap-3 px-8 py-4 rounded-xl bg-accent/20 hover:bg-accent/30 ring-1 ring-accent/50 hover:ring-accent text-white font-bold text-base transition-all duration-300 hover:scale-[1.03] shadow-lg shadow-accent/20 hover:shadow-accent/40"
            >
              <CaptainLogo size={24} />
              <span>{t('Chat with Captain ROCA', 'Parler à Captain ROCA', 'Hablar con Captain ROCA')}</span>
            </button>
            <a
              href="/bpjeps-tep-roadmap"
              className="flex items-center gap-2 px-8 py-4 rounded-xl bg-white/10 hover:bg-white/20 ring-1 ring-white/30 hover:ring-white/60 text-white font-bold text-base transition-all duration-300 hover:scale-[1.03] no-underline"
            >
              <Radio className="w-5 h-5" />
              <span>{t('BPJEPS Motonautisme Prep', 'Préparation BPJEPS Motonautisme', 'Preparación BPJEPS Motonáutica')}</span>
            </a>
          </div>
        </div>

      </section>

      {/* ─── RECOMMENDED JOURNEY (Ocean Navigator 6-step path) ─── */}
      <RecommendedJourney lang={lang as "en" | "fr" | "es"} />

      {/* Quick-access removed — replaced by the floating BottomActionBar (6 icons) */}

      {/* ─── 3 SERVICE CARDS (Cat Explorer style) ─── */}
      <section id="services" className="py-20 bg-background">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('Choose Your Path', 'Choisissez votre voie', 'Elige tu camino')}
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              {t('Three main pathways to start your maritime career.', 'Trois voies principales pour commencer votre carrière maritime.', 'Tres caminos principales para iniciar tu carrera marítima.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {/* Card 1 — Yacht Crew */}
            <button
              onClick={() => openChatWithPrompt(t("I want to work on yachts. Where do I start?", "Je veux travailler sur des yachts. Par où commencer ?", "Quiero trabajar en yates. ¿Por dónde empiezo?"))}
              className="group text-left p-8 rounded-2xl bg-card border border-border/50 hover:border-accent/50 transition-all duration-300 hover:shadow-xl hover:shadow-accent/10 hover:-translate-y-1"
            >
              <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-5 group-hover:bg-accent/20 transition-colors">
                <Ship className="w-7 h-7 text-accent" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">
                {t('Yacht Crew', 'Équipage Yacht', 'Tripulación Yate')}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {t('Deckhand, steward, captain… Start your superyacht career.', 'Matelot, steward, capitaine… Lancez votre carrière superyacht.', 'Marinero, steward, capitán… Inicia tu carrera en superyates.')}
              </p>
              <span className="text-sm font-semibold text-accent flex items-center gap-1 group-hover:gap-2 transition-all">
                {t('Learn more', 'En savoir plus', 'Saber más')}
                <ChevronRight className="w-4 h-4" />
              </span>
            </button>

            {/* Card 2 — Boat License */}
            <a
              href="/crr-zone1"
              className="group text-left p-8 rounded-2xl bg-card border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-1 no-underline"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                <Sailboat className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">
                {t('Boat License', 'Permis Bateau', 'Licencia Náutica')}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {t('CRR, coastal & offshore licenses — study with AI support.', 'CRR, permis côtier & hauturier — révisez avec l\'IA.', 'CRR, licencias costera y de altura — estudia con apoyo IA.')}
              </p>
              <span className="text-sm font-semibold text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                {t('Learn more', 'En savoir plus', 'Saber más')}
                <ChevronRight className="w-4 h-4" />
              </span>
            </a>

            {/* Card 3 — Watersports Instructor */}
            <button
              onClick={() => openChatWithPrompt(t("How do I become a watersports instructor?", "Comment devenir moniteur de sports nautiques ?", "¿Cómo me convierto en instructor de deportes acuáticos?"))}
              className="group text-left p-8 rounded-2xl bg-card border border-border/50 hover:border-neon-purple/50 transition-all duration-300 hover:shadow-xl hover:shadow-neon-purple/10 hover:-translate-y-1"
            >
              <div className="w-14 h-14 rounded-xl bg-neon-purple/10 flex items-center justify-center mb-5 group-hover:bg-neon-purple/20 transition-colors">
                <Users className="w-7 h-7 text-neon-purple" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">
                {t('Watersports Instructor', 'Moniteur Nautique', 'Instructor Acuático')}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {t('Jet-ski, wakeboard, paddle — teach on yachts and bases.', 'Jet-ski, wakeboard, paddle — enseignez sur yachts et bases.', 'Jet-ski, wakeboard, paddle — enseña en yates y bases.')}
              </p>
              <span className="text-sm font-semibold text-neon-purple flex items-center gap-1 group-hover:gap-2 transition-all">
                {t('Learn more', 'En savoir plus', 'Saber más')}
                <ChevronRight className="w-4 h-4" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* ─── WELCOME / WHAT IS CAPTAIN ROCA? ─── */}
      <section className="py-20 bg-card/30">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-[1fr_auto] gap-10 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                {t('What is Captain ROCA?', "C'est quoi Captain ROCA ?", '¿Qué es Captain ROCA?')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t(
                  'Captain ROCA is a free AI companion designed to guide you through the maritime world — from your first boat license to your first job on a yacht. Built specifically for dyslexic and visual learners, it replaces dense manuals with interactive, icon-based guidance.',
                  'Captain ROCA est un compagnon IA gratuit conçu pour vous guider dans le monde maritime — de votre premier permis bateau à votre premier emploi sur un yacht. Conçu spécialement pour les apprenants dyslexiques et visuels, il remplace les manuels denses par un guidage interactif basé sur les icônes.',
                  'Captain ROCA es un compañero IA gratuito diseñado para guiarte en el mundo marítimo — desde tu primera licencia náutica hasta tu primer trabajo en un yate. Creado especialmente para aprendices disléxicos y visuales, reemplaza los manuales densos con guías interactivas basadas en iconos.'
                )}
              </p>
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-success" />
                  {t('100% Free', '100% Gratuit', '100% Gratuito')}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  {t('Dyslexia-friendly', 'Adapté dyslexie', 'Adaptado dislexia')}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  {t('3 Languages', '3 Langues', '3 Idiomas')}
                </div>
              </div>
              <button
                onClick={() => { setPendingPrompt(undefined); setShowCaptainRoca(true); }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent/15 hover:bg-accent/25 ring-1 ring-accent/40 text-accent font-semibold transition-all duration-200"
              >
                <CaptainLogo size={20} />
                {t('Try it now — it\'s free', 'Essayez — c\'est gratuit', 'Pruébalo — es gratis')}
              </button>
            </div>
            <div className="flex justify-center">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center">
                <CaptainLogo size={80} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-center text-xl font-bold text-foreground mb-12">
            {t('How It Works', 'Comment ça marche', 'Cómo funciona')}
          </h2>
          <div className="grid grid-cols-3 gap-6 sm:gap-10">
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-accent/10 ring-1 ring-accent/30 flex items-center justify-center">
                <Compass className="w-6 h-6 text-accent" />
              </div>
              <span className="text-sm font-semibold text-foreground">{t('Tell us your goal', 'Dites-nous votre objectif', 'Cuéntanos tu objetivo')}</span>
            </div>
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-neon-purple/10 ring-1 ring-neon-purple/30 flex items-center justify-center">
                <Brain className="w-6 h-6 text-neon-purple" />
              </div>
              <span className="text-sm font-semibold text-foreground">{t('Get a personalized plan', 'Recevez un plan personnalisé', 'Recibe un plan personalizado')}</span>
            </div>
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-primary/10 ring-1 ring-primary/30 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-primary" />
              </div>
              <span className="text-sm font-semibold text-foreground">{t('Study & get certified', 'Étudiez & certifiez-vous', 'Estudia y certifícate')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHAT IS CRR? ─── */}
      <section className="py-20 bg-card/30">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-start gap-5 p-8 rounded-2xl bg-card border border-border/40">
            <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
              <Radio className="w-7 h-7 text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground mb-3">
                {t('What is the CRR?', "C'est quoi le CRR ?", '¿Qué es el CRR?')}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                {t(
                  'The CRR (Certificat Restreint de Radiotéléphoniste) is the French VHF maritime radio license. It\'s mandatory if you want to use a VHF radio on a boat — for safety calls, distress signals, and communication at sea.',
                  'Le CRR (Certificat Restreint de Radiotéléphoniste) est le permis radio VHF maritime français. Il est obligatoire pour utiliser une radio VHF à bord — pour les appels de sécurité, les signaux de détresse et la communication en mer.',
                  'El CRR (Certificat Restreint de Radiotéléphoniste) es la licencia francesa de radio marítima VHF. Es obligatorio para usar una radio VHF a bordo — para llamadas de seguridad, señales de socorro y comunicación en el mar.'
                )}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                {t(
                  'Internationally recognized as the Short Range Certificate (SRC), it\'s your entry ticket to maritime careers — from deckhand to captain.',
                  'Reconnu internationalement comme le Short Range Certificate (SRC), c\'est votre ticket d\'entrée pour les carrières maritimes — du matelot au capitaine.',
                  'Reconocido internacionalmente como el Short Range Certificate (SRC), es tu entrada a las carreras marítimas — de marinero a capitán.'
                )}
              </p>
              <a
                href="/crr-zone1"
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent/80 transition-colors"
              >
                {t('Start learning the CRR', 'Commencer à apprendre le CRR', 'Empieza a aprender el CRR')}
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHAT CAPTAIN ROCA DOES (4 Pillars) ─── */}
      <section className="py-20 bg-background">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('What Can Captain ROCA Do for You?', 'Que peut faire Captain ROCA pour vous ?', '¿Qué puede hacer Captain ROCA por ti?')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('Four pillars of support — from your first question to your first contract.', 'Quatre piliers de soutien — de votre première question à votre premier contrat.', 'Cuatro pilares de apoyo — desde tu primera pregunta hasta tu primer contrato.')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {corePillars.map((pillar, i) => (
              <Card key={i} className={`p-6 transition-all duration-300 border-l-4 ${pillar.borderAccent} bg-card hover:shadow-lg`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                    <pillar.icon className={`w-6 h-6 ${pillar.accent}`} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-foreground mb-2">{pillar.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{pillar.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COUNTRY INTELLIGENCE ─── */}
      <section className="py-20 bg-card/30">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              {t('Where Should You Go?', 'Où devriez-vous aller ?', '¿Adónde deberías ir?')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('Captain ROCA maps the best maritime markets in Europe.', 'Captain ROCA cartographie les meilleurs marchés maritimes en Europe.', 'Captain ROCA cartografía los mejores mercados marítimos de Europa.')}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {countrySpotlights.map((spot, i) => (
              <Card key={i} className="p-8 bg-card border border-border/50 hover:border-accent/40 transition-all duration-300 text-center hover:shadow-lg">
                <p className="text-4xl mb-4">{spot.flag}</p>
                <h4 className="text-lg font-bold text-foreground mb-2">{spot.country}</h4>
                <Badge variant="outline" className="mb-4 text-xs border-muted-foreground/30 text-muted-foreground">{spot.focus}</Badge>
                <p className="text-sm text-muted-foreground leading-relaxed">{spot.detail}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── YOUR LICENSES ─── */}
      <section className="py-20 bg-background">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('What Can You Do With Your License?', 'Que faire avec vos permis ?', '¿Qué puedes hacer con tu licencia?')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm">
              {t('Concrete opportunities unlocked by your nautical certifications.', 'Les opportunités concrètes ouvertes par vos certifications nautiques.', 'Oportunidades concretas con tus certificaciones náuticas.')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Card 1 — Permis Côtier + CRR */}
            <Card className="p-6 bg-card border border-border/50 border-t-4 border-t-accent/50 hover:border-t-accent transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Anchor className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm">Permis Côtier + CRR</h3>
                  <p className="text-[11px] text-muted-foreground">{t('Sea — up to 6 NM', 'Mer — jusqu\'à 6 milles', 'Mar — hasta 6 millas')}</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>{t('Drive a motorboat up to 6 NM from shelter', 'Conduire un bateau moteur jusqu\'à 6 milles d\'un abri', 'Conducir un barco a motor hasta 6 NM de refugio')}</li>
                <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>{t('Use VHF radio for safety at sea', 'Utiliser la VHF pour la sécurité en mer', 'Usar la radio VHF para seguridad')}</li>
              </ul>
              <p className="text-xs font-semibold text-foreground mb-2">{t('Opportunities:', 'Opportunités :', 'Oportunidades:')}</p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {[
                  t('Nautical base', 'Base nautique', 'Base náutica'),
                  t('Boat rental', 'Location bateau', 'Alquiler barcos'),
                  t('Jet-ski assistant', 'Assistant jet-ski', 'Asistente jet-ski'),
                ].map((opp, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] border-accent/30 text-accent">{opp}</Badge>
                ))}
              </div>
              <Button size="sm" variant="ghost" className="w-full text-accent hover:bg-accent/10" onClick={() => openChatWithPrompt(t('What jobs can I get with Permis Côtier and CRR?', 'Quels emplois avec le Permis Côtier et le CRR ?', '¿Qué trabajos puedo conseguir con la licencia costera y CRR?'))}>
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" />{t('Ask Captain ROCA', 'Demander à Captain ROCA', 'Preguntar a Captain ROCA')}
              </Button>
            </Card>

            {/* Card 2 — Permis Fluvial */}
            <Card className="p-6 bg-card border border-border/50 border-t-4 border-t-primary/50 hover:border-t-primary transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Waves className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm">Permis Fluvial</h3>
                  <p className="text-[11px] text-muted-foreground">{t('Rivers & canals', 'Rivières & canaux', 'Ríos y canales')}</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>{t('Navigate rivers, canals, and lakes', 'Naviguer sur rivières, canaux et lacs', 'Navegar por ríos, canales y lagos')}</li>
                <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>{t('Work in river tourism and hospitality', 'Travailler dans le tourisme fluvial', 'Trabajar en turismo fluvial')}</li>
              </ul>
              <p className="text-xs font-semibold text-foreground mb-2">{t('Opportunities:', 'Opportunités :', 'Oportunidades:')}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {[
                  t('River tourism', 'Tourisme fluvial', 'Turismo fluvial'),
                  t('Barge rental', 'Location péniche', 'Alquiler barcaza'),
                  t('River cruises', 'Croisières fluviales', 'Cruceros fluviales'),
                ].map((opp, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] border-primary/30 text-primary">{opp}</Badge>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground/60 mb-4">📍 Seine • Rhône • Canal du Midi • Loire</p>
              <Button size="sm" variant="ghost" className="w-full text-primary hover:bg-primary/10" onClick={() => openChatWithPrompt(t('What jobs can I get with Permis Fluvial?', 'Quels emplois avec le Permis Fluvial ?', '¿Qué trabajos puedo conseguir con la licencia fluvial?'))}>
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" />{t('Ask Captain ROCA', 'Demander à Captain ROCA', 'Preguntar a Captain ROCA')}
              </Button>
            </Card>

            {/* Card 3 — Next Level */}
            <Card className="p-6 bg-card border border-border/50 border-t-4 border-t-neon-purple/50 hover:border-t-neon-purple transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-neon-purple/10 flex items-center justify-center">
                  <Award className="w-5 h-5 text-neon-purple" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm">{t('First Steps → Next Level', 'Premiers pas → Niveau supérieur', 'Primeros pasos → Siguiente nivel')}</h3>
                  <p className="text-[11px] text-muted-foreground">{t('Build experience, then upgrade', 'Gagner en expérience, puis évoluer', 'Ganar experiencia, luego evolucionar')}</p>
                </div>
              </div>
              <p className="text-xs font-semibold text-foreground mb-2">{t('Start as:', 'Commencez comme :', 'Empieza como:')}</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground mb-4">
                <li className="flex items-start gap-2"><span className="text-neon-purple mt-0.5">•</span>{t('Marina assistant', 'Assistant marina', 'Asistente de marina')}</li>
                <li className="flex items-start gap-2"><span className="text-neon-purple mt-0.5">•</span>{t('Tourism boat crew', 'Équipage bateau tourisme', 'Tripulación barco turístico')}</li>
                <li className="flex items-start gap-2"><span className="text-neon-purple mt-0.5">•</span>{t('Beginner deckhand', 'Matelot débutant', 'Marinero principiante')}</li>
              </ul>
              <p className="text-xs font-semibold text-foreground mb-2">{t('Then upgrade to:', 'Puis évoluer vers :', 'Luego avanzar a:')}</p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {['Certificat Matelot', 'Capitaine 200', 'BPJEPS', 'STCW'].map((cert, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] border-neon-purple/30 text-neon-purple">{cert}</Badge>
                ))}
              </div>
              <Button size="sm" variant="ghost" className="w-full text-neon-purple hover:bg-neon-purple/10" onClick={() => openChatWithPrompt(t('How do I progress from beginner to professional captain?', 'Comment passer de débutant à capitaine professionnel ?', '¿Cómo paso de principiante a capitán profesional?'))}>
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" />{t('Ask Captain ROCA', 'Demander à Captain ROCA', 'Preguntar a Captain ROCA')}
              </Button>
            </Card>
          </div>

          <p className="text-center text-sm text-muted-foreground italic">
            🚪 {t('These licenses are your entry door to the maritime world.', 'Ces permis sont votre porte d\'entrée dans le monde maritime.', 'Estas licencias son tu puerta de entrada al mundo marítimo.')}
          </p>
        </div>
      </section>

      {/* ─── CHOOSE YOUR MARITIME PATH (8 paths) ─── */}
      <section className="py-20 bg-card/30">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('All Maritime Paths', 'Tous les chemins maritimes', 'Todos los caminos marítimos')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('Select a path → see what you need → ask Captain ROCA.', 'Choisissez un parcours → voyez les prérequis → demandez à Captain ROCA.', 'Elige un camino → mira los requisitos → pregunta a Captain ROCA.')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {maritimePaths.map(path => (
              <button
                key={path.id}
                onClick={() => setSelectedPath(selectedPath === path.id ? null : path.id)}
                className={`p-4 rounded-xl border-2 text-center transition-all duration-300 hover:scale-[1.02] ${
                  selectedPath === path.id ? path.activeColor : path.color
                }`}
              >
                <span className="text-2xl block mb-1.5">{path.icon}</span>
                <span className="font-bold text-foreground text-[11px] sm:text-xs leading-tight block">{path.label}</span>
              </button>
            ))}
          </div>

          {/* Path Detail Panel */}
          {selectedPathData && (
            <Card className="p-6 bg-card border border-border/50 border-l-4 border-l-accent/50 mb-12 animate-in fade-in slide-in-from-top-2 duration-300">
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">{selectedPathData.description}</p>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="font-bold text-accent text-xs mb-2">{t('Required Licenses', 'Licences requises', 'Licencias requeridas')}</h4>
                  <p className="text-muted-foreground text-sm">{selectedPathData.licenses}</p>
                </div>
                <div>
                  <h4 className="font-bold text-primary text-xs mb-2">{t('First Steps', 'Premiers pas', 'Primeros pasos')}</h4>
                  <p className="text-muted-foreground text-sm">{selectedPathData.firstSteps}</p>
                </div>
              </div>
              <div className="text-center">
                <Button
                  size="sm"
                  onClick={() => openChatWithPrompt(`${t('I want to become a', 'Je veux devenir', 'Quiero ser')} ${selectedPathData.label}. ${t('What do I need?', 'De quoi ai-je besoin ?', '¿Qué necesito?')}`)}
                  className="bg-primary text-primary-foreground"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {t('Ask Captain ROCA', 'Demander à Captain ROCA', 'Preguntar a Captain ROCA')}
                </Button>
              </div>
            </Card>
          )}

          {/* 3000 Miles + French vs RYA */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Card className="p-6 bg-card border border-border/50 border-t-4 border-t-accent/40">
              <div className="flex items-center gap-3 mb-4">
                <Compass className="w-6 h-6 text-accent" />
                <h3 className="font-bold text-foreground text-lg">
                  {t('The 3000 Miles Question', 'La question des 3000 milles', 'La pregunta de las 3000 millas')}
                </h3>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                {t(
                  'To become a professional captain, you must accumulate real navigation miles. Sea miles are the real currency of experience.',
                  'Pour devenir capitaine professionnel, vous devez accumuler de vrais milles de navigation. Les milles marins sont la vraie monnaie de l\'expérience.',
                  'Para ser capitán profesional, debes acumular millas de navegación reales. Las millas marinas son la verdadera moneda de la experiencia.'
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  t('Sailing deliveries', 'Convoyages', 'Entregas de veleros'),
                  t('Yacht transfers', 'Transferts yacht', 'Transferencias de yates'),
                  t('Deckhand work', 'Travail matelot', 'Trabajo marinero'),
                  t('Sailing schools', 'Écoles de voile', 'Escuelas de vela'),
                ].map((way, i) => (
                  <Badge key={i} variant="outline" className="border-accent/30 text-accent text-xs">{way}</Badge>
                ))}
              </div>
            </Card>

            <Card className="p-6 bg-card border border-border/50 border-t-4 border-t-neon-purple/40">
              <div className="flex items-center gap-3 mb-4">
                <GraduationCap className="w-6 h-6 text-neon-purple" />
                <h3 className="font-bold text-foreground text-lg">
                  {t('French vs RYA Training', 'Formation française vs RYA', 'Formación francesa vs RYA')}
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-bold text-foreground mb-1">🇫🇷 {t('French', 'Français', 'Francés')}</p>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {t('Highly regulated, strong safety standards, recognized commercially.', 'Très réglementé, normes de sécurité élevées, reconnu commercialement.', 'Muy regulado, altos estándares de seguridad, reconocido comercialmente.')}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground mb-1">🇬🇧 RYA</p>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {t('Faster progression, very practical, widely recognized in yachting.', 'Progression plus rapide, très pratique, largement reconnu dans le yachting.', 'Progresión más rápida, muy práctica, ampliamente reconocida en yachting.')}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground/70 mt-3 italic">
                {t('Both systems can complement each other.', 'Les deux systèmes peuvent se compléter.', 'Ambos sistemas pueden complementarse.')}
              </p>
            </Card>
          </div>

          {/* Strategic Starting Point */}
          <Card className="p-6 bg-card border border-border/50 border-l-4 border-l-warning/50 mb-12 text-center">
            <MapPin className="w-8 h-8 text-warning mx-auto mb-3" />
            <h3 className="font-bold text-foreground text-lg mb-2">
              Antibes — {t('"The Silicon Valley of Yachting"', '"La Silicon Valley du Yachting"', '"El Silicon Valley del Yachting"')}
            </h3>
            <p className="text-muted-foreground text-sm max-w-2xl mx-auto leading-relaxed">
              {t(
                'Largest yacht concentration in Europe. Crew agencies, training schools, seasonal employment. Your best entry point for a yachting career.',
                'Plus grande concentration de yachts en Europe. Agences d\'équipage, écoles de formation, emploi saisonnier. Votre meilleur point d\'entrée.',
                'Mayor concentración de yates en Europa. Agencias de tripulación, escuelas de formación, empleo estacional. Tu mejor punto de entrada.'
              )}
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {['Antibes', 'Nice', 'Monaco'].map(city => (
                <Badge key={city} className="bg-warning/10 text-warning border-warning/30 text-xs">{city}</Badge>
              ))}
            </div>
          </Card>

          {/* Global Maritime Hubs */}
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-foreground mb-2">
              {t('Global Maritime Hubs', 'Hubs maritimes mondiaux', 'Centros marítimos globales')}
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { zone: t('Mediterranean', 'Méditerranée', 'Mediterráneo'), spots: 'Monaco • Antibes • Mallorca • Ibiza • Croatia • Greece', emoji: '🏛' },
              { zone: t('Caribbean', 'Caraïbes', 'Caribe'), spots: 'Saint Martin • Guadeloupe • Martinique • BVI', emoji: '🌴' },
              { zone: t('Indian Ocean', 'Océan Indien', 'Océano Índico'), spots: 'Seychelles • Mauritius • Reunion Island', emoji: '🐚' },
              { zone: t('Pacific', 'Pacifique', 'Pacífico'), spots: 'French Polynesia • Australia • New Zealand', emoji: '🌏' },
            ].map((hub, i) => (
              <Card key={i} className="p-4 bg-card border border-border/50 text-center hover:border-primary/40 transition-all duration-300">
                <span className="text-2xl block mb-2">{hub.emoji}</span>
                <h4 className="font-bold text-foreground text-sm mb-2">{hub.zone}</h4>
                <p className="text-muted-foreground text-xs leading-relaxed">{hub.spots}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DYSLEXIC LEADERS CAROUSEL ─── */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-16">
        <Suspense fallback={<div className="min-h-[300px]" />}>
          <DyslexicLeadersCarousel />
        </Suspense>
      </div>

      {/* ─── LAZY-LOADED HEAVY SECTIONS ─── */}
      <Suspense fallback={<div className="min-h-[200px]" />}>
        <div className="max-w-5xl mx-auto px-6 py-20 space-y-20">
          <HistoricShipsExplorer t={t} onAskCaptain={openChatWithPrompt} />
          <DayInTheLife lang={lang} onAskCaptain={openChatWithPrompt} />
          <MaritimeSalaryCalculator lang={lang} onAskCaptain={openChatWithPrompt} />
        </div>
      </Suspense>

      <Suspense fallback={<div className="min-h-[200px]" />}>
        <WatersportActivitiesCatalog lang={lang} onAskCaptain={openChatWithPrompt} />
      </Suspense>

      <div id="jobs">
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <SeasonalMaritimeJobsMap lang={lang} onAskCaptain={openChatWithPrompt} />
        </Suspense>
      </div>

      <Suspense fallback={<div className="min-h-[200px]" />}>
        <MaritimeJobLinks lang={lang} onAskCaptain={openChatWithPrompt} />
      </Suspense>

      {/* ─── FRANCE MARITIME EVENTS CALENDAR ─── */}
      <Suspense fallback={<div className="min-h-[200px]" />}>
        <FranceMaritimeEventsCalendar lang={lang} t={t} onAskCaptain={openChatWithPrompt} />
      </Suspense>
      <section className="py-20 bg-background">
        <div className="max-w-5xl mx-auto px-6">
          <Card className="p-10 bg-card border border-border/50 max-w-4xl mx-auto text-center">
            <Ship className="w-12 h-12 text-neon-purple mx-auto mb-6" />
            <h4 className="text-2xl font-bold text-foreground mb-4">
              {t('Born from Experience. Powered by AI.', 'Né de l\'expérience. Propulsé par l\'IA.', 'Nacido de la experiencia. Impulsado por IA.')}
            </h4>
            <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-6">
              {t(
                "Captain ROCA isn't a generic chatbot. It's trained on real maritime journey — from struggling with French maritime exams as a dyslexic thinker, to working on yachts across the Mediterranean.",
                "Captain ROCA n'est pas un chatbot générique. Il est entraîné sur un vrai parcours maritime — des examens maritimes français en tant que penseur dyslexique au travail sur les yachts en Méditerranée.",
                "Captain ROCA no es un chatbot genérico. Está entrenado con un recorrido marítimo real — desde los exámenes marítimos franceses como pensador disléxico hasta trabajar en yates por el Mediterráneo."
              )}
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <Badge className="bg-accent/10 text-accent border-accent/20 px-4 py-1">#DyslexSEA</Badge>
              <Badge className="bg-primary/10 text-primary border-primary/20 px-4 py-1">#MaritimeCareers</Badge>
              <Badge className="bg-neon-purple/10 text-neon-purple border-neon-purple/20 px-4 py-1">#DyslexicThinker</Badge>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                onClick={() => { setPendingPrompt(undefined); setShowCaptainRoca(true); }}
                className="bg-primary text-primary-foreground"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                {t('Start Your Journey', 'Commencez votre voyage', 'Comienza tu viaje')}
              </Button>
              <Button 
                variant="outline"
                size="lg"
                asChild
              >
                <a href="/crr-zone1">
                  <BookOpen className="w-5 h-5 mr-2" />
                  {t('Explore CRR Training', 'Explorer la formation CRR', 'Explorar la formación CRR')}
                </a>
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* ─── IDEA BOX ─── */}
      <div id="idea-box">
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <IdeaBox lang={lang} t={t} />
        </Suspense>
      </div>

      {/* ─── SUPPORT CREDITS DONATION ─── */}
      <Suspense fallback={<div className="min-h-[200px]" />}>
        <SupportCreditsDonation lang={lang} t={t} />
      </Suspense>

      {/* ─── DYSLEXIA SOS ─── */}
      <div id="dyslexia-sos">
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <DyslexiaSOS lang={lang} t={t} onAskCaptain={openChatWithPrompt} />
        </Suspense>
      </div>

      {/* ─── CAPTAIN ROCA OFFICIAL LINKS ─── */}
      <CaptainRocaLinks t={t} />

      {/* ─── FOOTER ─── */}
      <Footer t={t} />

      {/* ─── Captain ROCA AI Modal ─── */}
      {showCaptainRoca && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl h-[85vh] sm:h-[80vh] bg-background rounded-2xl shadow-2xl ring-1 ring-accent/30 overflow-hidden">
            <button
              onClick={() => { setShowCaptainRoca(false); setPendingPrompt(undefined); }}
              className="absolute top-3 right-3 z-10 p-2 rounded-full bg-muted/80 hover:bg-muted text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <CaptainRocaAI 
              onClose={() => { setShowCaptainRoca(false); setPendingPrompt(undefined); }}
              initialPrompt={pendingPrompt}
            />
          </div>
        </div>
      )}

      {/* ─── CRR Flash Ticker ─── */}
      <CRRFlashTicker />

      {/* All floating tools are now grouped inside the cinema film reel */}
      <FloatingFilmReel />
    </div>
  );
};

export default Index;
